import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarDays, MapPin, Clock, ArrowUpRight, Ticket, PlaneTakeoff, ChevronDown, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { IMAGES } from "../lib/images";
import api from "../lib/api";
import { Reveal, useSEO } from "../components/site/Reveal";
import FullImage from "../components/site/FullImage";
import Newsletter from "../components/site/Newsletter";
import EventRegistrationModal from "../components/site/EventRegistrationModal";
import { useCurrency } from "../context/CurrencyContext";

function FAQs({ faqs }) {
  const [open, setOpen] = React.useState(null);
  if (!faqs || faqs.length === 0) return null;
  return (
    <div className="mt-4 border-t border-parchment pt-4" data-testid="event-faqs">
      <p className="text-xs uppercase tracking-widest text-gold mb-2">Frequently asked</p>
      <div className="space-y-1.5">
        {faqs.map((fq, i) => (
          <div key={i} className="rounded-lg bg-sand/60 overflow-hidden">
            <button onClick={() => setOpen(open === i ? null : i)} data-testid={`faq-toggle-${i}`} className="w-full flex items-center justify-between gap-3 text-left px-4 py-2.5 text-sm font-semibold text-ink hover:bg-sand transition-colors">
              {fq.q}
              <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {open === i && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
                  <p className="px-4 pb-3 text-sm text-ink-muted leading-relaxed whitespace-pre-line">{fq.a}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  );
}

function WaitlistModal({ event, onClose }) {
  const [f, setF] = React.useState({ full_name: "", email: "", phone: "" });
  const [busy, setBusy] = React.useState(false);
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email);
  const submit = async () => {
    if (!f.full_name.trim() || !emailOk) { toast.error("Please enter your name and a valid email."); return; }
    setBusy(true);
    try {
      await api.post(`/events/${event.id}/waitlist`, { event_id: event.id, ...f });
      toast.success("You're on the waitlist! We'll email you if a spot opens.");
      onClose();
    } catch { toast.error("Could not join the waitlist."); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center p-4" data-testid="waitlist-modal" onClick={onClose}>
      <div className="absolute inset-0 bg-night/80 backdrop-blur-sm" />
      <div className="relative w-full max-w-md rounded-2xl border border-parchment bg-cream p-7" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} data-testid="waitlist-close" className="absolute top-4 right-4 grid place-items-center w-9 h-9 rounded-lg hover:bg-sand"><X className="w-5 h-5" /></button>
        <p className="uppercase tracking-[0.2em] text-xs text-gold mb-1">Waitlist</p>
        <h3 className="font-serif text-2xl text-ink leading-tight mb-1">{event.title}</h3>
        <p className="text-sm text-ink-muted mb-5">This trip is fully booked. Join the waitlist and we'll email you the moment a place opens up.</p>
        <div className="space-y-3">
          <input value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} placeholder="Full name" data-testid="waitlist-name-input" className="w-full rounded-lg bg-white border border-parchment px-3.5 py-2.5 text-ink focus:outline-none focus:border-forest" />
          <input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email" data-testid="waitlist-email-input" className="w-full rounded-lg bg-white border border-parchment px-3.5 py-2.5 text-ink focus:outline-none focus:border-forest" />
          <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Phone (optional)" data-testid="waitlist-phone-input" className="w-full rounded-lg bg-white border border-parchment px-3.5 py-2.5 text-ink focus:outline-none focus:border-forest" />
          <button onClick={submit} disabled={busy} data-testid="waitlist-submit" className="w-full inline-flex items-center justify-center gap-2 bg-forest text-cream font-semibold py-3 rounded-full hover:bg-forest-light transition-colors disabled:opacity-60">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Joining…</> : "Join the waitlist"}
          </button>
        </div>
      </div>
    </div>
  );
}

const MONTHS_SHORT = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

const parseDate = (d) => {
  if (!d) return null;
  const parts = d.split("-").map(Number);
  if (parts.length < 3 || parts.some(isNaN)) return null;
  return new Date(parts[0], parts[1] - 1, parts[2]);
};

function DateBadge({ date, past }) {
  const dt = parseDate(date);
  return (
    <div className={`shrink-0 w-16 rounded-xl overflow-hidden text-center border ${past ? "border-parchment opacity-70" : "border-gold/40"}`}>
      <div className={`text-[11px] font-bold py-1 ${past ? "bg-ink/10 text-ink-muted" : "bg-gold text-night"}`}>
        {dt ? MONTHS_SHORT[dt.getMonth()] : "TBA"}
      </div>
      <div className="py-1.5 bg-cream">
        <span className="font-serif text-2xl text-ink">{dt ? dt.getDate() : "–"}</span>
      </div>
    </div>
  );
}

function EventCard({ e, past, onRegister, onWaitlist }) {
  const { format } = useCurrency();
  const dt = parseDate(e.date);
  const dateText = dt ? dt.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : "Date to be announced";
  return (
    <div
      className={`flex gap-5 rounded-2xl border bg-cream p-5 transition-all duration-300 ${past ? "border-parchment" : "border-parchment hover:border-gold hover:-translate-y-0.5"}`}
      data-testid={`event-card-${e.id}`}
    >
      <DateBadge date={e.date} past={past} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] uppercase tracking-widest text-gold">{e.category || "Event"}</span>
          {e.requires_registration && !past && !e.is_full && <span className="text-[10px] uppercase tracking-widest text-forest">· Registration open</span>}
          {e.requires_registration && !past && typeof e.spots_left === "number" && !e.is_full && (
            <span className="text-[10px] uppercase tracking-widest text-gold font-bold" data-testid={`event-spots-${e.id}`}>· Only {e.spots_left} spot{e.spots_left === 1 ? "" : "s"} left</span>
          )}
          {e.requires_registration && !past && e.is_full && <span className="text-[10px] uppercase tracking-widest text-red-500 font-bold" data-testid={`event-full-${e.id}`}>· Fully booked</span>}
          {past && <span className="text-[10px] uppercase tracking-widest text-ink-muted">· Past</span>}
        </div>
        <h3 className="font-serif text-2xl text-ink leading-snug">{e.title}</h3>
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-ink-muted mt-2">
          <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-gold" />{dateText}</span>
          {e.time && <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-gold" />{e.time}</span>}
          {e.location && <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-gold" />{e.location}</span>}
        </div>
        {e.image && <FullImage src={e.image} alt={e.title} className="h-52 rounded-xl mt-4" />}
        {e.description && <p className="text-[15px] text-ink-muted leading-relaxed mt-3 whitespace-pre-line">{e.description}</p>}
        <div className="flex flex-wrap items-center gap-3 mt-4">
          {e.requires_registration && !past && (
            e.is_full ? (
              <button onClick={() => onWaitlist(e)} data-testid={`event-waitlist-${e.id}`} className="inline-flex items-center gap-2 bg-ink text-cream text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-ink/90 transition-colors">
                Fully booked · Join waitlist
              </button>
            ) : (
              <button onClick={() => onRegister(e)} data-testid={`event-register-${e.id}`} className="inline-flex items-center gap-2 bg-gold text-night text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-gold-light transition-colors">
                <PlaneTakeoff className="w-4 h-4" /> Register {Number(e.registration_fee) > 0 ? `· ${format(Number(e.registration_fee))}` : ""}
              </button>
            )
          )}
          {e.link && !past && (
            <a href={e.link} target="_blank" rel="noreferrer" data-testid={`event-link-${e.id}`} className="inline-flex items-center gap-2 bg-forest text-cream text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-forest-light transition-colors">
              <Ticket className="w-4 h-4" /> Details & Tickets <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
        {!past && <FAQs faqs={e.faqs} />}
      </div>
    </div>
  );
}

export default function Events() {
  useSEO(
    "Events Calendar",
    "Upcoming ÀsÀNigHT Worldwide concerts, exhibitions, festivals and cultural gatherings. See what's next and join the celebration."
  );

  const [events, setEvents] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [banner, setBanner] = React.useState("");
  const [registering, setRegistering] = React.useState(null);
  const [waitlisting, setWaitlisting] = React.useState(null);
  const [tripPhotos, setTripPhotos] = React.useState([]);
  React.useEffect(() => { api.get("/site-settings").then((r) => { if (r.data?.banner_events) setBanner(r.data.banner_events); }).catch(() => {}); }, []);

  React.useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    api.get("/trip-photos").then((r) => setTripPhotos(r.data || [])).catch(() => {});
  }, []);

  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const isUpcoming = (e) => {
    const dt = parseDate(e.date);
    return !dt || dt >= start;
  };
  const upcoming = events.filter(isUpcoming);
  const past = events.filter((e) => !isUpcoming(e)).reverse();

  return (
    <div>
      {/* HERO */}
      <header className="relative min-h-[50vh] flex items-end grain-overlay">
        <div className="absolute inset-0">
          <img src={banner || IMAGES.drummers} alt="Events" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-night via-night/80 to-night/40" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 pb-16 w-full">
          <p className="uppercase tracking-[0.3em] text-xs text-gold-soft mb-5 inline-flex items-center gap-2">
            <CalendarDays className="w-4 h-4" /> What's On
          </p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="font-serif text-5xl lg:text-6xl text-cream leading-tight text-shadow-hero">
            Events Calendar
          </motion.h1>
          <p className="mt-5 text-cream/80 text-lg max-w-2xl">
            Concerts, exhibitions, festivals and cultural gatherings — see what's coming up and
            join us in celebration.
          </p>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-20">
        {loading ? (
          <p className="text-ink-muted text-center py-16">Loading…</p>
        ) : events.length === 0 ? (
          <Reveal>
            <div className="text-center border border-dashed border-parchment rounded-2xl py-14 px-6 bg-sand" data-testid="events-empty">
              <span className="grid place-items-center w-16 h-16 rounded-full bg-gold/15 text-gold mx-auto mb-4"><CalendarDays className="w-7 h-7" /></span>
              <p className="font-serif text-2xl text-ink mb-2">No Events Scheduled Yet</p>
              <p className="text-ink-muted max-w-xl mx-auto">Our upcoming concerts and exhibitions will appear here soon. Check back to plan your visit!</p>
            </div>
          </Reveal>
        ) : (
          <>
            {upcoming.length > 0 && (
              <section className="mb-16">
                <Reveal><h2 className="font-serif text-3xl md:text-4xl text-ink mb-8">Upcoming</h2></Reveal>
                <div className="space-y-5" data-testid="events-upcoming">
                  {upcoming.map((e, i) => (
                    <Reveal key={e.id} delay={i * 0.06}><EventCard e={e} onRegister={setRegistering} onWaitlist={setWaitlisting} /></Reveal>
                  ))}
                </div>
              </section>
            )}
            {past.length > 0 && (
              <section>
                <Reveal><h2 className="font-serif text-2xl text-ink mb-6">Past Events</h2></Reveal>
                <div className="space-y-5">
                  {past.map((e, i) => (
                    <Reveal key={e.id} delay={i * 0.05}><EventCard e={e} past /></Reveal>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>

      {/* MOMENTS FROM OUR TRIPS */}
      {tripPhotos.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 pb-20" data-testid="trip-gallery">
          <Reveal>
            <div className="text-center mb-10">
              <p className="uppercase tracking-[0.25em] text-xs text-gold mb-2">Gallery</p>
              <h2 className="font-serif text-3xl md:text-4xl text-ink">Moments from our trips</h2>
              <p className="text-ink-muted mt-2 max-w-xl mx-auto">A look back at the journeys, faces and places from our past excursions.</p>
            </div>
          </Reveal>
          <div className="columns-2 md:columns-3 gap-4 [column-fill:_balance]">
            {tripPhotos.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.05}>
                <figure className="mb-4 break-inside-avoid rounded-xl overflow-hidden border border-parchment bg-cream" data-testid={`trip-photo-${p.id}`}>
                  {p.media_type === "video" ? (
                    <video src={p.image_url} poster={p.poster || undefined} controls playsInline preload={p.poster ? "none" : "metadata"} className="w-full block bg-night" data-testid={`trip-video-${p.id}`} />
                  ) : (
                    <img src={p.image_url} alt={p.caption || "Trip moment"} loading="lazy" className="w-full block" />
                  )}
                  {p.caption && <figcaption className="px-3 py-2 text-sm text-ink-muted">{p.caption}</figcaption>}
                </figure>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <Newsletter />
      {registering && <EventRegistrationModal event={registering} onClose={() => setRegistering(null)} />}
      {waitlisting && <WaitlistModal event={waitlisting} onClose={() => setWaitlisting(null)} />}
    </div>
  );
}
